import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { HomeOutlined, UserOutlined, LogoutOutlined } from '@ant-design/icons';
import { Dropdown, Avatar, Space } from 'antd';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import cleverooLogo from './assets/cleveroo.svg';
import StudentDashboard from './components/StudentDashboard';
import Login from './components/Login';
import Signup from './components/Signup';
import Profile from './components/Profile';
import Settings from './components/Settings';
import ImageUploadTest from './components/test/ImageUploadTest';
import OnboardingWizard from './components/OnboardingWizard';
import BillingPage from './components/billing/BillingPage';
import BillingSuccess from './components/billing/BillingSuccess';
import BillingCancel from './components/billing/BillingCancel';
import FamilyPage from './components/billing/FamilyPage';
import TrySubjectPicker from './components/try/TrySubjectPicker';
import TryQuiz from './components/try/TryQuiz';
import axios from 'axios';
import 'antd/dist/reset.css';
import { captureUtm, captureRef } from './api/utm';
import { track } from './api/analytics';

// ─── UTM capture + session visit event ───────────────────────────────────────
// Run once when the JS bundle first executes (before any component mounts).
// captureUtm/captureRef are safe to call in module scope — they never throw.
captureUtm();
captureRef();

const VISIT_FIRED_KEY = 'cleveroo_visit_fired';

/** Fire a `visit` event at most once per browser-tab session. */
function fireVisitOnce(): void {
  try {
    if (sessionStorage.getItem(VISIT_FIRED_KEY)) return;
    sessionStorage.setItem(VISIT_FIRED_KEY, '1');
  } catch {
    // sessionStorage unavailable — still fire the event but don't guard.
  }
  track('visit', {
    properties: {
      path: typeof window !== 'undefined' ? window.location.pathname : null,
    },
  });
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

// Navigation component that uses the router location
const Navigation: React.FC = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  
  const handleLogout = () => {
    logout();
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Sign Out',
      onClick: handleLogout,
    },
  ];
  
  return (
    <nav 
      className="bg-white shadow-lg sticky top-0 z-50 border-b border-gray-200"
      style={{
        backgroundColor: 'white',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        position: 'sticky',
        top: 0,
        zIndex: 50,
        borderBottom: '1px solid #e5e7eb'
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" style={{ maxWidth: '80rem', margin: '0 auto', padding: '0 16px' }}>
        <div className="flex flex-row justify-between items-center h-16" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '64px' }}>
          <div className="flex flex-row items-center" style={{ display: 'flex', alignItems: 'center' }}>
            <Link to="/" className="flex-shrink-0 flex flex-row items-center hover:opacity-80 transition-opacity duration-200" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              <img 
                src={cleverooLogo} 
                alt="Cleveroo Logo" 
                style={{ width: '40px', height: '40px' }}
              />
              <span className="ml-3 text-2xl font-bold text-gray-800 font-baloo" style={{ marginLeft: '12px', fontSize: '24px', fontWeight: 'bold', color: '#1f2937', fontFamily: 'Baloo 2, cursive' }}>
                cleveroo
              </span>
            </Link>
          </div>
          
          {/* Navigation Menu */}
          <div className="flex flex-row items-center space-x-4" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div className="flex flex-row items-center space-x-1" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Link to="/" className="group" style={{ textDecoration: 'none' }}>
                <div 
                  className={`
                    px-5 py-2.5 rounded-xl font-medium transition-all duration-200 flex flex-row items-center space-x-2
                    ${location.pathname === '/' 
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg transform scale-105' 
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
                    }
                  `}
                  style={location.pathname === '/' ? {
                    background: 'linear-gradient(to right, #3b82f6, #9333ea)',
                    color: 'white',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                    transform: 'scale(1.05)',
                    padding: '10px 20px',
                    borderRadius: '12px',
                    fontWeight: '500'
                  } : {
                    color: '#6b7280',
                    padding: '10px 20px',
                    borderRadius: '12px',
                    fontWeight: '500'
                  }}
                >
                  <HomeOutlined className={`text-lg ${location.pathname === '/' ? 'text-white' : 'text-gray-600 group-hover:text-gray-800'}`} />
                  <span>Dashboard</span>
                </div>
              </Link>
            </div>

            {/* User Menu */}
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" arrow>
              <Space className="cursor-pointer hover:bg-gray-50 rounded-lg px-3 py-2 transition-colors">
                <Avatar 
                  size="default" 
                  icon={<UserOutlined />}
                  src={user?.avatar}
                  style={{ backgroundColor: '#1890ff' }}
                />
                <span className="text-gray-700 font-medium cvr-legacy-nav-name">{user?.name}</span>
              </Space>
            </Dropdown>
          </div>
        </div>
      </div>
    </nav>
  );
};

// 主应用内容
const AppContent: React.FC = () => {
  const { isAuthenticated, login, user } = useAuth();
  const location = useLocation();
  const [roleCheckDone, setRoleCheckDone] = React.useState(false);

  // Fire `visit` once per tab session on first render.
  React.useEffect(() => {
    fireVisitOnce();
  }, []);

  // Onboarding gate: null = unknown/loading, true/false once /me resolves.
  const [onboardingCompleted, setOnboardingCompleted] = React.useState<boolean | null>(null);

  const fetchOnboardingStatus = React.useCallback(async () => {
    try {
      const token =
        localStorage.getItem('cleveroo_token') ||
        localStorage.getItem('accessToken') ||
        '';
      if (!token) {
        setOnboardingCompleted(null);
        return;
      }
      const res = await axios.get(`${API_BASE_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOnboardingCompleted(!!res.data?.data?.onboardingCompleted);
    } catch (err) {
      // On failure, don't hard-block the user behind onboarding — treat as
      // completed so an API hiccup can't lock them out of the dashboard.
      console.error('Failed to fetch onboarding status:', err);
      setOnboardingCompleted(true);
    }
  }, []);

  React.useEffect(() => {
    if (isAuthenticated && user) {
      fetchOnboardingStatus();
    } else {
      setOnboardingCompleted(null);
    }
  }, [isAuthenticated, user, fetchOnboardingStatus]);

  // Every portal page owns its own chrome (cream dashboard nav, quiz top bar,
  // "← Dashboard" pill pages). The legacy white <Navigation /> bar only exists
  // for old dev/utility pages — showing it anywhere else stacks a second,
  // inconsistent header with a different avatar (ROO-186). Allow-list it.
  const legacyNavRoutes = ['/test/upload'];
  const hideLegacyNav = !legacyNavRoutes.includes(location.pathname);

  const handleAuth = (userData: any) => {
    const userInfo = {
      id: userData.id,
      email: userData.email,
      name: userData.name,
      provider: userData.provider || 'local',
      role: userData.role,
      studentId: userData.studentId,
    };

    login(
      userInfo,
      userData.accessToken || userData.token
    );

    // Redirect to home after successful auth
    window.location.href = '/';
  };

  const navigateToSignup = () => {
    window.location.href = '/signup';
  };

  const navigateToLogin = () => {
    window.location.href = '/login';
  };

  React.useEffect(() => {
    if (isAuthenticated && user) {
      setRoleCheckDone(true);
    } else if (!isAuthenticated) {
      setRoleCheckDone(true);
    }
  }, [isAuthenticated, user]);

  // Public /demo routes — bypass auth, render dashboard with mock data.
  // Useful for screenshots, stakeholder previews, sales conversations.
  if (location.pathname === '/demo' || location.pathname === '/demo-empty') {
    const state = location.pathname === '/demo-empty' ? 'empty' : 'active';
    return (
      <>
        <StudentDashboard forcedPreview={state} />
        <a
          href="https://portal.cleverooedu.com.au/"
          style={{
            position: 'fixed',
            top: 12,
            right: 12,
            zIndex: 100,
            background: '#F4C534',
            color: '#14593C',
            padding: '6px 12px',
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            fontFamily: '"Bricolage Grotesque", system-ui, sans-serif',
            textDecoration: 'none',
            boxShadow: '0 6px 14px -6px rgba(20,89,60,.45)',
          }}
          title="Demo mode — mock data, not real student state"
        >
          Demo mode
        </a>
      </>
    );
  }

  // Public /try routes — no-signup "try 3 real questions" hook (ROO-142).
  // Must bypass auth entirely: this is the top of the acquisition funnel and
  // has to work for a completely anonymous, logged-out visitor.
  if (location.pathname === '/try' || location.pathname.startsWith('/try/')) {
    return (
      <Routes>
        <Route path="/try" element={<TrySubjectPicker />} />
        <Route path="/try/:subject" element={<TryQuiz />} />
      </Routes>
    );
  }

  // Show signup page at /signup route (even when not authenticated)
  if (!isAuthenticated) {
    if (location.pathname === '/signup') {
      return <Signup onSignup={handleAuth} onSwitchToLogin={navigateToLogin} />;
    }
    return <Login onLogin={handleAuth} onSwitchToSignup={navigateToSignup} />;
  }

  // Show loading while checking role or onboarding status
  if (!roleCheckDone || onboardingCompleted === null) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh'
      }}>
        <div>Checking user access...</div>
      </div>
    );
  }

  // Onboarding gate: a brand-new user must finish the wizard before the
  // dashboard (or any other authed route) is reachable.
  if (onboardingCompleted === false) {
    return (
      <OnboardingWizard
        onComplete={() => {
          setOnboardingCompleted(true);
          window.location.href = '/';
        }}
      />
    );
  }

  return (
    <div>
      {!hideLegacyNav && <Navigation />}

      {/* Main Content */}
      <Routes>
        <Route path="/" element={<StudentDashboard />} />
        {/* Dashboard sections + nested student views are real routes so the URL
            reflects the screen and Back/Forward / deep-links work (ROO-119). */}
        {/* Progress-over-time on real assessment history (ROO-166). */}
        <Route path="/progress" element={<StudentDashboard />} />
        <Route path="/achievements" element={<StudentDashboard />} />
        <Route path="/resources" element={<StudentDashboard />} />
        <Route path="/resources/:slug" element={<StudentDashboard />} />
        <Route path="/placement" element={<StudentDashboard />} />
        <Route path="/report" element={<StudentDashboard />} />
        {/* Recommended set (wrong-answer-driven) must precede the :skill param (ROO-160). */}
        <Route path="/practice/recommended" element={<StudentDashboard />} />
        {/* 5-question cross-domain mini check-in (ROO-171). */}
        <Route path="/practice/checkin" element={<StudentDashboard />} />
        <Route path="/practice/:skill" element={<StudentDashboard />} />
        <Route path="/wrong-questions" element={<StudentDashboard />} />
        {/* Full-length timed mock exams (ROO-169). */}
        <Route path="/mock-exams" element={<StudentDashboard />} />
        <Route path="/assessment" element={<StudentDashboard />} />
        <Route path="/test" element={<StudentDashboard />} />
        <Route path="/result" element={<StudentDashboard />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/oc-practice/*" element={<Navigate to="/" replace />} />
        <Route path="/test/upload" element={<ImageUploadTest />} />
        {/* Billing / subscription routes */}
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/pricing" element={<BillingPage />} />
        <Route path="/billing/success" element={<BillingSuccess />} />
        <Route path="/billing/cancel" element={<BillingCancel />} />
        {/* Family management */}
        <Route path="/family" element={<FamilyPage />} />
      </Routes>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
};

export default App;
